import { writable } from 'svelte/store';
import collect from 'collect.js';
import config from '@/config.js';
// Models
import MarketGroupSet from '@/Models/MarketGroupSet.js';

/**
 * Our Store can be used 2 ways in svelte components
 * 1) ($ prefix) $StoreName to reference writable store collection with all collect.js methods available
 * 2) (Direct) StoreName.method() to call a method on the store
 */
const init = () => {
	let _store;
	let meta;

	const { subscribe, set, update } = writable(collect());

	subscribe((current) => (_store = current));

	const methods = {
		getPagination() {
			return meta && meta.pagination;
		},

		getMarketGroupSetsForSport(sportId, pagination = null) {
			if (!config.SPORT_ENABLED) return;

			const marketGroupSet = new MarketGroupSet();
			marketGroupSet.resourceUrl = () => {
				return `${marketGroupSet.baseUrl()}/sports/${sportId}/market_group_sets`;
			};
			let results = pagination ? marketGroupSet.paginate(pagination.size, pagination.number) : marketGroupSet.get();
			return results
				.then((result) => {
					meta = result.meta;
					set(collect(result.data));
				})
				.catch((e) => {
					console.error(e);
				});
		},
		getMarketGroupSet(id) {
			if (!config.SPORT_ENABLED) return;

			const marketGroupSet = new MarketGroupSet();
			return marketGroupSet
				.with(['market_set_groups'])
				.find(id)
				.then((result) => {
					result.market_groups = result.market_set_groups.data.map((marketGroup) => {
						return {
							market_group_id: marketGroup.market_group_id.toString(),
							name: marketGroup.name,
							order: marketGroup.order,
							is_primary: marketGroup.is_primary,
							is_key: marketGroup.is_key,
							default_open: marketGroup.default_open
						};
					});
					set(result);
				});
		},

		newMarketGroupSet() {
			let marketGroupSet = new MarketGroupSet();
			marketGroupSet = marketGroupSet.hydrate({
				id: null,
				name: '',
				sport: '',
				is_default: false,
				type: 'market_group_sets'
			});
			marketGroupSet.market_groups = collect();
			set(marketGroupSet);
		},

		saveMarketGroupSet() {
			return _store.id
				? _store.update(preparePayload())
				: _store.create({ sport_id: _store.sport, ...preparePayload() }).then((result) => {
						update((marketGroupSet) => {
							marketGroupSet.id = result.id;
							return marketGroupSet;
						});
						return result;
					});
		},

		updateName(name) {
			update((marketGroupSet) => {
				marketGroupSet.name = name;
				return marketGroupSet;
			});
		},

		updateIsDefault(isDefault) {
			update((marketGroupSet) => {
				marketGroupSet.is_default = isDefault;
				return marketGroupSet;
			});
		},

		updateSelectedSport(id) {
			update((marketGroupSet) => {
				marketGroupSet.sport = id;
				return marketGroupSet;
			});
		},

		addMarketGroup(marketGroup) {
			update((marketGroupSet) => {
				let nextOrder = (marketGroupSet.market_groups.sortBy('order').pluck('order').last() || 0) + 1;
				// don't add the marketGroup if we already have it
				if (marketGroupSet.market_groups.where('market_group_id', marketGroup.id).count() === 0) {
					marketGroupSet.market_groups.push({
						market_group_id: marketGroup.id,
						name: marketGroup.name,
						order: nextOrder,
						is_primary: false,
						is_key: false,
						default_open: false
					});
				}
				return marketGroupSet;
			});
		},

		removeMarketGroupId(id) {
			update((marketGroup) => {
				marketGroup.market_groups = marketGroup.market_groups.reject((mg) => mg.market_group_id === id);
				return marketGroup;
			});
			this.reorderMarketGroups();
		},

		swapMarketGroupOrder(from, to) {
			let mg = _store.market_groups;
			const fromOrder = from.order;
			const toOrder = to.order;
			mg = mg.sortBy('order').map((marketGroup) => {
				if (marketGroup.market_group_id === from.market_group_id) {
					marketGroup.order = toOrder;
				} else if (marketGroup.market_group_id === to.market_group_id) {
					marketGroup.order = fromOrder;
				}
				return marketGroup;
			});

			update((marketGroup) => {
				marketGroup.market_groups = mg;
				return marketGroup;
			});
		},

		reorderMarketGroups() {
			update((marketGroupSet) => {
				let order = 1;
				marketGroupSet.market_groups = marketGroupSet.market_groups.sortBy('order').map((marketGroup) => {
					marketGroup.order = order;
					order++;
					return marketGroup;
				});
				return marketGroupSet;
			});
		},

		resetMarketGroups() {
			update((marketGroupSet) => {
				marketGroupSet.market_groups = collect();
				return marketGroupSet;
			});
		},

		resetStore() {
			set(collect());
		}
	};

	function preparePayload() {
		const mg = _store.market_groups;
		return {
			name: _store.name,
			is_default: _store.is_default,
			market_groups: mg
				.map((marketGroup) => {
					return {
						market_group_id: parseInt(marketGroup.market_group_id),
						order: marketGroup.order,
						is_primary: marketGroup.is_primary,
						is_key: marketGroup.is_key,
						default_open: marketGroup.default_open
					};
				})
				.toArray()
		};
	}

	return {
		subscribe,
		update,
		set,
		...methods
	};
};

export default init();
